import React, { useEffect, useMemo } from 'react';
import { Flex, FormControl, FormLabel, Grid, Heading, Input, useToast } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import DataConsentCheckbox from 'ts/common/components/DataConsentCheckbox';
import ResponsiveButtonGroup from 'ts/common/components/ResponsiveButtonGroup';
import { ButtonProps } from 'ts/common/components/gallery';
import { useTranslate } from 'ts/common/hooks';
import { useClientBookingExperience } from 'client_react/booking/ClientBookingExperience';
import { ROUTE } from 'client_react/booking/common';
import BookingSessionHeading from 'client_react/booking/components/BookingSessionHeading';
import { isBookingSessionError } from 'client_react/booking/useBookingSession';
import { isContractError } from 'client_react/common/hooks/useContract';

interface ContactFormFields {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

const ContactInformationPage = () => {
    const {
        formState: { isSubmitting, isValid },
        handleSubmit,
        register
    } = useForm<ContactFormFields>({ mode: 'onTouched' });

    const {
        bookingSession,
        bookingSessionType,
        createContract,
        isPending,
        updateBookingSession,
        submitPayment,
        navigateWithSession
    } = useClientBookingExperience();

    const t = useTranslate('booking.contactInformationPage');

    const errorToast = useToast({
        status: 'error'
    });

    const handleSubmitForm = handleSubmit(async (contactFieldValues: ContactFormFields) => {
        if (!bookingSessionType || !bookingSession) {
            return;
        }

        const response = await updateBookingSession({
            sessionTypePublicId: bookingSessionType.publicId,
            contact: {
                type: 'contact',
                ...contactFieldValues
            }
        });

        if (!response || isBookingSessionError(response)) {
            errorToast({
                title: t('apiError')
            });

            return;
        }

        if (bookingSessionType.contractTemplateId) {
            const contractResponse = await createContract({
                data: {
                    contactId: response.contactId,
                    contractTemplateId: bookingSessionType.contractTemplateId,
                    expirationDate: bookingSession.startTime,
                    name: `${bookingSessionType.contractTemplateName}`,
                    shootDate: bookingSession.startTime,
                    type: 'contract'
                }
            });

            if (!contractResponse || isContractError(contractResponse)) {
                errorToast({
                    title: t('apiError')
                });

                return;
            }

            const patchContractIdResponse = await updateBookingSession({
                contractPublicId: contractResponse.publicId,
                sessionTypePublicId: bookingSessionType.publicId
            });

            if (!patchContractIdResponse || isBookingSessionError(patchContractIdResponse)) {
                errorToast({
                    title: t('apiError')
                });

                return;
            }

            navigateWithSession(ROUTE.CONTRACT);
        } else {
            if (bookingSession?.stripePrice === 0) {
                await submitPayment({ paymentAmountType: 'full' });
                navigateWithSession(ROUTE.CONFIRMATION);
            } else {
                navigateWithSession(ROUTE.PAYMENT);
            }
        }
    });

    useEffect(() => {
        if (isPending) {
            return;
        }

        if (!bookingSession) {
            navigateWithSession(ROUTE.DATE_AND_TIME);
        } else if (!bookingSession.isIncomplete) {
            navigateWithSession(ROUTE.CONFIRMATION);
        }
    }, [bookingSession, isPending, navigateWithSession]);

    const buttons = useMemo<ButtonProps[]>(
        () => [
            {
                text: t('back'),
                onClick: () => navigateWithSession(ROUTE.DATE_AND_TIME),
                variant: 'outline primary'
            },
            {
                text: t('continue'),
                isDisabled: isPending || isSubmitting || !isValid,
                isLoading: isSubmitting,
                variant: 'primary',
                type: 'submit'
            }
        ],
        [isPending, isSubmitting, isValid, navigateWithSession, t]
    );

    if (!bookingSession || !bookingSessionType) {
        return null;
    }

    return (
        <>
            <BookingSessionHeading
                bookingSession={bookingSession}
                bookingSessionType={bookingSessionType}
            />
            <Flex as="form" direction="column" gap="24px" onSubmit={handleSubmitForm}>
                <Heading as="h3" size="lg" margin={0}>
                    {t('heading')}
                </Heading>
                <Grid gap="16px 24px" gridTemplateColumns="repeat(auto-fill, minmax(400px, 1fr))">
                    <FormControl isRequired maxWidth="calc(100vw - 32px)" paddingBottom={0}>
                        <FormLabel>{t('firstName')}</FormLabel>
                        <Input {...register('firstName', { required: true })} />
                    </FormControl>
                    <FormControl isRequired maxWidth="calc(100vw - 32px)" paddingBottom={0}>
                        <FormLabel>{t('lastName')}</FormLabel>
                        <Input {...register('lastName', { required: true })} />
                    </FormControl>
                    <FormControl isRequired maxWidth="calc(100vw - 32px)" paddingBottom={0}>
                        <FormLabel>{t('email')}</FormLabel>
                        <Input type="email" {...register('email', { required: true })} />
                    </FormControl>
                    <FormControl isRequired maxWidth="calc(100vw - 32px)" paddingBottom={0}>
                        <FormLabel>{t('phone')}</FormLabel>
                        <Input type="tel" {...register('phone', { required: true })} />
                    </FormControl>
                </Grid>
                {bookingSession.isSubjectToGdpr && (
                    <DataConsentCheckbox register={register} translate={t} />
                )}
                <ResponsiveButtonGroup buttons={buttons} />
            </Flex>
        </>
    );
};

export default ContactInformationPage;
